import { Upload } from '@mui/icons-material'
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Theme,
	Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { percent } from 'csx'
import { FormikValues, useFormik } from 'formik'
import { observer } from 'mobx-react'
import { useMemo, useState } from 'react'
import Dropzone, { DropzoneState } from 'react-dropzone'
import * as yup from 'yup'
import { ObjectShape } from 'yup/lib/object'
import { FixedHeaderWithHint } from '../../utils/HOC/FixedHeaders'

export type fieldValue = string | null

type UploadDialogProps<T> = {
	uploadTypeName?: string
	acceptedFileExtensions?: string | string[]
	additionalFields?: [{
		name: string
		required?: boolean
		validationTests?: [{
			isValid: (value?: fieldValue) => boolean
			invalidMessage?: string
		}]
		initialValue?: fieldValue
		getAutofillValue?: (item: T) => fieldValue
		hint?: string
	}]
	onCancel: () => void
	onConfirm: (uploadedItem: T, values?: Record<string, fieldValue>) => void
}

export const UploadDialog = observer(
	<T,>(props: UploadDialogProps<T>) => {

		const [uploadData, setUploadData] = useState<T | undefined>(undefined)
		const styles = useStyles()

		const validationSchema = useMemo(() => !props.additionalFields ? undefined :
			yup.object(props.additionalFields.reduce(
				(acc, field) => {
					let schema = yup.string()
					if (field.required)
						schema = schema.required(`${field.name} is a required field`)

					field.validationTests?.forEach((vt) =>
						schema = schema.test(
							'is-valid',
							vt.invalidMessage ?? "Invalid " + field.name,
							vt.isValid
						)
					)

					acc[field.name] = schema
					return acc
				},
				{} as ObjectShape
			)),
			[props.additionalFields],
		)

		const formik = useFormik({
			initialValues: props.additionalFields?.reduce(
				(acc, field) => {
					acc[field.name] = field.initialValue
					return acc
				}, { file: false } as FormikValues
			) ?? { file: false },
			validationSchema: validationSchema,
			onSubmit: (values) => {
				if (uploadData == undefined)
					throw new Error('upload data missing')

				props.onConfirm(uploadData, values)
			},
		})

		const handleDrop = (files: File[]) => {
			if (files.length === 0) return

			const file = files[0]
			const reader = new FileReader()
			reader.addEventListener('load', (event) => {
				const result = event.target?.result

				if (result === undefined || result === null) {
					console.warn('failed to read result')
					return
				}

				const output: T = JSON.parse(result as string)

				formik.setTouched(props.additionalFields?.reduce(
					(acc, field) => {
						if (!field.getAutofillValue)
							return acc
						acc[field.name] = true
						return acc
					}, { file: true } as Record<string, boolean>
				) ?? { file: true })
				formik.setValues(props.additionalFields?.reduce(
					(acc, field) => {
						if (!field.getAutofillValue)
							return acc
						acc[field.name] = field.getAutofillValue(output)
						return acc
					}, {} as Record<string, fieldValue>
				) ?? {})

				setUploadData(output)
			})

			reader.readAsText(file)
		}

		return (
			<>
				<DialogTitle>Upload {props.uploadTypeName}</DialogTitle>
				<DialogContent>
					<Dropzone onDrop={handleDrop} accept={props.acceptedFileExtensions}>
						{({
							acceptedFiles,
							getRootProps,
							getInputProps,
						}: DropzoneState) => (
							<Box
								component="section"
								flex={'1'}
								className={clsx(
									styles.verticalSpacing,
									styles.enabledDragAndDrop,
								)}
							>
								<Box height={percent(100)} {...getRootProps()}>
									<input {...getInputProps()} />
									<div className={styles.dragAndDropBody}>
										<Box
											display="flex"
											flexDirection="column"
											justifyContent="center"
											alignItems="center"
											height={'100%'}
										>
											<Box
												overflow={'auto'}
												width={percent(100)}
												textAlign={'center'}
											>
												<Box
													flex="1"
													overflow={'hidden'}
													display={'flex'}
													flexDirection={'column'}
													justifyContent={'center'}
													alignItems={'center'}
												>
													<Upload />
													{acceptedFiles.length == 0 ? (
														<Typography>{props.uploadTypeName} File</Typography>
													) : (
														acceptedFiles.map((f) => (
															<Typography key={f.name}>{f.name}</Typography>
														))
													)}
												</Box>
											</Box>
										</Box>
									</div>
								</Box>
							</Box>
						)}
					</Dropzone>

					{props.additionalFields?.map((field, i) =>
						<FixedHeaderWithHint
							key={i}
							label={field.name}
							className={styles.textFieldStyle}
							hint={
								formik.errors[field.name] as string | string[] ?? field.hint
							}
							hintColor={formik.errors[field.name] ? 'red' : undefined}
						>
							<TextField
								fullWidth
								id={field.name}
								name={field.name}
								value={formik.values[field.name] ?? ''}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								autoComplete="off"
								error={
									!!formik.errors[field.name] && !!formik.touched[field.name]
								}
							/>
						</FixedHeaderWithHint>
					)}

					<DialogActions>
						<Button color="primary" onClick={props.onCancel}>
							Cancel
						</Button>
						<Button
							onClick={formik.submitForm}
							disabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
							color="primary"
							variant="contained"
						>
							Upload
						</Button>
					</DialogActions>
				</DialogContent>
			</>
		)
	})



const useStyles = makeStyles((theme: Theme) => ({
	mainDiv: {
		display: 'flex',
		flexDirection: 'column',
	},

	textFieldStyle: {
		margin: theme.spacing(2, 0),
	},

	verticalSpacing: {
		height: '50%',
		flex: 3,
	},
	enabledDragAndDrop: {
		cursor: 'pointer',
	},

	disabledDragAndDrop: {
		opacity: percent(60),
	},

	dragAndDropBody: {
		height: percent(100),
		width: percent(100),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',

		justifyContent: 'center',
		alignItems: 'center',

		borderRadius: theme.shape.borderRadius,
		borderWidth: 2,
		borderStyle: 'dashed',
		borderColor: theme.palette.text.secondary,

		overflowWrap: 'break-word',

		padding: theme.spacing(2),

		[theme.breakpoints.up('sm')]: {
			padding: theme.spacing(5),
		},
	},
}))